import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { PasswordModule } from 'primeng/password';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { RippleModule } from 'primeng/ripple';
import { TabViewModule } from 'primeng/tabview';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';

import { AuthService } from '@core/service/auth.service';
import { ToastService } from '@core/service/toast.service';
import { IUser } from '@shared/models/user';
import { FormErrorComponent } from '@shared/components/form-error/form-error.component';
import { RoleLabelPipe } from '@shared/pipes/role-label.pipe';

type ChangePasswordFormShape = {
  oldPassword: FormControl<string>;
  newPassword: FormControl<string>;
  confirmPassword: FormControl<string>;
};

function passwordsMatch(group: AbstractControl): ValidationErrors | null {
  const a = group.get('newPassword')?.value;
  const b = group.get('confirmPassword')?.value;
  return a && b && a !== b ? { passwordsMismatch: true } : null;
}

@Component({
  selector: 'app-settings',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    DividerModule,
    PasswordModule,
    ProgressSpinnerModule,
    RippleModule,
    TabViewModule,
    TagModule,
    ToastModule,
    FormErrorComponent,
    RoleLabelPipe,
  ],
  templateUrl: './settings.component.html',
  styleUrl: './settings.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SettingsComponent implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly toast = inject(ToastService);
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly profileLoading = signal(false);
  protected readonly profile = signal<IUser | null>(null);
  protected readonly submitting = signal(false);

  changeForm!: FormGroup<ChangePasswordFormShape>;

  ngOnInit(): void {
    this.changeForm = this.fb.nonNullable.group<ChangePasswordFormShape>(
      {
        oldPassword: this.fb.nonNullable.control('', Validators.required),
        newPassword: this.fb.nonNullable.control('', [Validators.required, Validators.minLength(8)]),
        confirmPassword: this.fb.nonNullable.control('', Validators.required),
      },
      { validators: passwordsMatch },
    );

    this.loadProfile();
  }

  private loadProfile(): void {
    this.profileLoading.set(true);
    this.authService
      .getProfile()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.profile.set(res?.body ?? null);
          this.profileLoading.set(false);
        },
        error: () => this.profileLoading.set(false),
      });
  }

  submit(): void {
    if (this.changeForm.invalid) {
      this.changeForm.markAllAsTouched();
      return;
    }
    this.submitting.set(true);
    const { oldPassword, newPassword } = this.changeForm.getRawValue();
    this.authService
      .changePassword({ oldPassword, password: newPassword })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.submitting.set(false);
          this.toast.showSuccess('Password changed', res?.message ?? 'Your password has been updated.');
          this.changeForm.reset();
        },
        error: () => this.submitting.set(false),
      });
  }
}
